import type { ErrorInfo } from 'react';

const DEFAULT_MAX_DISPLAY_LENGTH = 200;

export type AppCrashLogContext = {
  url?: string;
  sessionId?: string;
};

function toSingleLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function buildDisplaySummary(
  error: Error,
  errorInfo?: ErrorInfo | null
): string {
  const componentFrame = errorInfo?.componentStack
    ?.split('\n')
    .map(line => line.trim())
    .find(line => line.length > 0);

  const stackFrame = error.stack
    ?.split('\n')
    .map(line => line.trim())
    .find(line => line.length > 0 && !line.startsWith(error.name));

  const parts = [
    error.name,
    error.message,
    componentFrame ?? stackFrame
  ].filter(Boolean);

  return toSingleLine(parts.join(' · '));
}

function resolveAppCrashLogContext(
  context?: AppCrashLogContext
): AppCrashLogContext {
  if (context) {
    return context;
  }

  return {
    url: window.location.href,
    sessionId:
      'sessionId' in window
        ? String((window as Window & { sessionId?: string }).sessionId ?? '')
        : undefined
  };
}

function formatAppCrashClipboard(
  error: Error,
  errorInfo: ErrorInfo | null | undefined,
  context?: AppCrashLogContext
): string {
  const { url, sessionId } = resolveAppCrashLogContext(context);
  const lines: string[] = [`Error: ${error.name} — ${error.message}`, ''];

  if (url) {
    lines.push(`URL: ${url}`);
  }
  if (sessionId) {
    lines.push(`Session: ${sessionId}`);
  }
  if (url || sessionId) {
    lines.push('');
  }

  lines.push('Stack:');
  lines.push(error.stack?.trim() || '(not available)');

  const componentStack = errorInfo?.componentStack?.trim();
  if (componentStack) {
    lines.push('', 'Component stack:', componentStack);
  }

  return lines.join('\n');
}

/**
 * Builds a one-line truncated summary for UI and a detailed multiline log for clipboard.
 */
export function formatAppCrashErrorLog(
  error: Error,
  errorInfo?: ErrorInfo | null,
  maxDisplayLength = DEFAULT_MAX_DISPLAY_LENGTH,
  context?: AppCrashLogContext
): { display: string; clipboard: string } {
  const summary = buildDisplaySummary(error, errorInfo);
  const display =
    summary.length > maxDisplayLength
      ? `${summary.slice(0, maxDisplayLength - 1)}…`
      : summary;

  return {
    display,
    clipboard: formatAppCrashClipboard(error, errorInfo, context)
  };
}

export function resolveAppCrashDashboardHref(
  pathname = window.location.pathname
): string | null {
  const match = pathname.match(/^\/app\/([^/]+)/);
  if (match) {
    return `/app/${match[1]}/dashboard`;
  }
  return null;
}
